import { getJson, sendJson } from '@/lib/api';

/**
 * Season and occasion tags on programs (Roy's portal list, item 7). Roy keeps
 * the list under Catalog → Season tags and tags programs on their page; the
 * client portal's calendar shows them as "Ideas for …".
 */
export interface SeasonTag {
  readonly _id: string;
  readonly name: string;
  /** Months of the year, 1 to 12, ascending. */
  readonly months: readonly number[];
  /** "June to August", worded by the API. */
  readonly monthsLabel: string;
  readonly description: string | null;
  readonly isActive: boolean;
  readonly programCount: number;
}

/** What a create or an edit sends. */
export interface SeasonTagDraft {
  readonly name: string;
  readonly months: readonly number[];
  readonly description: string;
}

/** A program carrying a tag. */
export interface TaggedProgram {
  readonly id: string;
  readonly name: string;
}

/**
 * Roles that may add, edit and assign tags: the same as program editing,
 * which the API allows admins and superadmins only (`PUT /programs/:id`).
 */
export const SEASON_TAG_EDITOR_ROLES: readonly string[] = ['admin', 'superadmin'];

/** Whether a staff role may change tags and assign them. */
export function canEditSeasonTags(role: string | undefined): boolean {
  return role !== undefined && SEASON_TAG_EDITOR_ROLES.includes(role);
}

/** The months as the picker offers them. */
export const MONTH_OPTIONS: readonly { readonly value: string; readonly label: string }[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((label, index) => ({ value: String(index + 1), label }));

export const SEASON_TAGS_QUERY_KEY = ['season-tags'] as const;

/** Every tag, retired ones included. The API seeds the starting list on first read. */
export async function listSeasonTags(signal?: AbortSignal): Promise<readonly SeasonTag[]> {
  return getJson<SeasonTag[]>('/season-tags', undefined, signal);
}

/** The programs carrying one tag. */
export async function listTaggedPrograms(tagId: string, signal?: AbortSignal): Promise<readonly TaggedProgram[]> {
  return getJson<TaggedProgram[]>(`/season-tags/${tagId}/programs`, undefined, signal);
}

/** Adds a tag. */
export async function createSeasonTag(draft: SeasonTagDraft): Promise<SeasonTag> {
  return sendJson<SeasonTag>('post', '/season-tags', {
    name: draft.name.trim(),
    months: draft.months,
    ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
  });
}

/** Renames a tag, changes its months or note, or retires or restores it. */
export async function updateSeasonTag(
  id: string,
  patch: Partial<SeasonTagDraft> & { readonly isActive?: boolean },
): Promise<SeasonTag> {
  return sendJson<SeasonTag>('patch', `/season-tags/${id}`, {
    ...patch,
    ...(patch.name === undefined ? {} : { name: patch.name.trim() }),
    ...(patch.description === undefined ? {} : { description: patch.description.trim() }),
  });
}
